import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BarChart3, Box, Lock, Phone, ShieldCheck, Tag, Users } from 'lucide-react';
import { paymentsAPI, tenantAPI } from '../api/client';
import { SiteHeader } from '../components/AuthShell';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiError } from '../lib/host';
import { CALL_CHARGES_NOTE, formatRupees } from '../lib/plans';
import { payInvoice } from '../lib/razorpayCheckout';
import usePlans from '../lib/usePlans';
import '../components/AuthShell.css';
import './Payment.css';

/**
 * Confirm a fixed-price plan: billing cycle (if the plan offers both), seats, an optional coupon, and the total before
 * GST. Confirming starts the plan's invoice; with online payment on (Razorpay), its Checkout opens for that invoice and
 * the server checks the payment. Card details are only ever entered in Razorpay's Checkout. Without online payment,
 * TeleCalX emails the invoice.
 *
 * A new company (customer approval) pays here before anything else; once paid it waits for TeleCalX's approval.
 */
const HIGHLIGHTS = [
  ['Business Phone System', 'Professional calling for your team', Phone],
  ['AI Receptionist', 'Never miss a customer call', Users],
  ['Advanced Analytics', 'Insights to grow your business', BarChart3],
  ['Secure & Reliable', 'Enterprise-grade security and uptime', ShieldCheck],
];
const AWAITING_APPROVAL ='Payment successful! Your subscription is complete and is awaiting admin approval. You will receive an email notification once our team approves your account.';

export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, reloadTenant, onboarding } = useTenant();
  const { logout } = useAuth();
  const isNew = !!onboarding && onboarding !== 'approved';
  const [online, setOnline] = useState(null); // Razorpay Checkout available
  const [done, setDone] = useState(''); // a new company's payment went through, or its plan is saved
  const { plans, loaded } = usePlans();
  const plan = plans.find((p) => p.code === location.state?.plan && p.buttonEnabled && p.priceType === 'fixed');
  const [users, setUsers] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [code, setCode] = useState('');
  const [coupon, setCoupon] = useState(null); // {code, kind, value, summary}
  const [couponError, setCouponError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loaded && !plan) navigate('/subscription', { replace: true });
  }, [loaded, plan, navigate]);
  useEffect(() => {
    tenantAPI.get().then((res) => setUsers(res.data.member_count)).catch(() => setUsers(1));
    paymentsAPI.available().then((res) => setOnline(!!res.data.enabled)).catch(() => setOnline(false));
  }, []);

  if (!loaded || !plan) return null; // wait for the current price from the catalog
  const chosen = cycle || (plan.cycle === 'yearly' ? 'yearly' : 'monthly');
  const seats = Math.max(users || 1, plan.minUsers || 1);
  const perSeat = chosen === 'yearly' ? plan.yearlyPrice : plan.price;
  const subtotal = perSeat * seats;
  const off = !coupon ? 0 : coupon.kind === 'percent' ? Math.round(subtotal * coupon.value) / 100 : Math.min(subtotal, coupon.value);

  const applyCoupon = async (e) => {
    e.preventDefault();
    setCouponError('');
    try {
      const res = await tenantAPI.checkCoupon(code, plan.code);
      setCoupon(res.data);
    } catch (err) {
      setCoupon(null);
      setCouponError(apiError(err, 'That coupon code isn\'t valid'));
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    let saved = false;
    try {
      await tenantAPI.setSubscription(plan.code, { billingCycle: chosen, coupon: coupon?.code });
      saved = true;
      // The plan's unpaid invoice (the server sets its amount), paid in Razorpay's Checkout and checked by the server.
      const due = online && (await tenantAPI.invoices()).data.invoices.find((i) => i.plan === plan.code && ['open', 'failed'].includes(i.status));
      const result = due ? await payInvoice(due.id) : null;
      if (isNew) {
        if (result?.status === 'paid') setDone(AWAITING_APPROVAL);
        else if (result) setError('The payment wasn\'t completed. Your plan is saved: pay to continue.');
        else setDone('Your plan is saved. TeleCalX will email your invoice with how to pay; our team reviews your account once it is paid.');
        setBusy(false);
        return;
      }
      await reloadTenant();
      const paid = result?.status === 'paid' ? ' Payment received, thank you!' : result ? ' You can pay its invoice from Billing & Plan.' : '';
      navigate('/', { replace: true, state: { success: `${tenant.name} is now on ${plan.name}.${paid}` } });
    } catch (err) {
      setError(err.response ? apiError(err, 'Could not change the plan. Please try again.')
        : `${err.message}${saved ? ' Your plan is saved: pay to continue.' : ''}`);
      setBusy(false);
    }
  };

  return (
    <div className="tcx-auth">
      <SiteHeader>
        <button type="button" className="tcx-back" onClick={() => navigate('/subscription')}><ArrowLeft size={14} />Change plan</button>
      </SiteHeader>
      <main className="tcx-checkout">
        <section className="tcx-checkout-side">
          <span className="tcx-eyebrow"><i />TELECALX WORKSPACE</span>
          <h1>Power Your<br />Business Communication<br />with TeleCalX</h1>
          <p>A modern cloud communication platform to help your team connect, engage and grow — anytime, anywhere.</p>
          <div className="tcx-checkout-showcase">
            <ul>
              {HIGHLIGHTS.map(([title, sub, Icon]) => (
                <li key={title}><span><Icon size={20} /></span><div><b>{title}</b><small>{sub}</small></div></li>
              ))}
            </ul>
            <img src="/brand/checkout-showcase.webp" alt="" width={534} height={382} />
          </div>
          <svg className="tcx-checkout-wave" viewBox="0 0 1000 120" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 40 C 220 110, 420 120, 620 80 S 900 10, 1000 0" />
          </svg>
        </section>
        <div className="tcx-checkout-main">
          <div className="auth-card tcx-order">
            <div className="tcx-order-head">
              <span><Box size={22} /></span>
              <h2>Order summary</h2>
            </div>
            {plan.cycle === 'both' && (
              <div className="tcx-cycle" role="radiogroup" aria-label="Billing cycle">
                {['monthly', 'yearly'].map((c) => (
                  <label key={c} className={chosen === c ? 'on' : ''}>
                    <input type="radio" name="cycle" checked={chosen === c} onChange={() => setCycle(c)} />
                    {c === 'monthly' ? 'Monthly' : `Yearly${plan.annualDiscount ? ` (save ${plan.annualDiscount}%)` : ''}`}
                  </label>
                ))}
              </div>
            )}
            <dl>
              <dt>Plan</dt><dd>{plan.name}</dd>
              <dt>Price</dt><dd>{formatRupees(perSeat)} per user / {chosen === 'yearly' ? 'year' : 'month'}</dd>
              <dt>Users</dt><dd>{users == null ? '…' : seats}{plan.minUsers > 1 && <small> (minimum {plan.minUsers})</small>}</dd>
              {coupon && <><dt>Coupon {coupon.code}</dt><dd>− {formatRupees(off)}</dd></>}
              <dt className="total">{chosen === 'yearly' ? 'Yearly' : 'Monthly'} total</dt>
              <dd className="total">{users == null ? '…' : formatRupees(subtotal - off)}</dd>
            </dl>
            {plan.allowCoupons && (
              <form className="tcx-coupon" onSubmit={applyCoupon}>
                <label className="tcx-coupon-field">
                  <Tag size={18} />
                  <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCouponError(''); }} placeholder="Coupon code" aria-label="Coupon code" />
                </label>
                <button type="submit" className="tcx-coupon-apply" disabled={!code.trim()}>Apply</button>
              </form>
            )}
            {coupon && <p className="tcx-hint">{coupon.summary}{coupon.periods === 1 ? ' on your first invoice' : coupon.periods ? ` for ${coupon.periods} invoices` : ' on every invoice'}.</p>}
            {couponError && <div className="error-message">{couponError}</div>}
            <p className="tcx-hint">{CALL_CHARGES_NOTE} Adding users later adds {formatRupees(perSeat)} per user.</p>
            <div className="tcx-pay-note">
              <span><ShieldCheck size={20} /></span>
              {online ? (
                <p><b>Pay securely with Razorpay</b>You can pay using card, UPI, net banking or wallet. The amount is set by TeleCalX, and your payment is confirmed by our server.</p>
              ) : (
                <p><b>Online payment is coming soon</b>Nothing is charged now: your plan starts today and TeleCalX will email your invoice with how to pay.</p>
              )}
            </div>
            {error && <div className="error-message">{error}</div>}
            {done ? (
              <>
                <div className="success-message" role="status">{done}</div>
                <p className="auth-link"><button type="button" className="tcx-link-btn" onClick={() => logout().finally(() => navigate('/login', { replace: true }))}>Back to login</button></p>
              </>
            ) : (
              <>
                <button type="button" className="submit-btn" disabled={busy || users == null || online == null} onClick={confirm}>
                  {busy ? 'Confirming…' : <>{online && <Lock size={18} />}{online ? `Confirm and pay for ${plan.name}` : `Confirm ${plan.name}`}<ArrowRight size={18} /></>}
                </button>
                <p className="auth-link"><button type="button" className="tcx-link-btn" onClick={() => navigate('/subscription')}>Choose a different plan</button></p>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
